<template>
  <div class="enhanced-payment-form">
    <!-- Card Number Field -->
    <div>
      <label :style="{'font-family': fontFamily, 'color': textColor}">
        {{ $t('payment_request.creditCardNumber') }}
      </label>
      <div class="card-input-container">
        <input
          ref="cardNumberInput"
          :style="{'color': textColor, 'border-color': getBorderColor('cardNumber')}"
          :class="{'error': validationErrors.cardNumber, 'valid': isCardNumberValid}"
          class="form-control card-number-input"
          placeholder="1234 5678 9012 3456"
          v-model="cardNumber"
          @input="onCardNumberChange"
          @blur="validateCardNumber"
          maxlength="23"
          autocomplete="cc-number"
        />
        <div class="card-brand-icon" v-if="cardBrand">
          <img :src="getCardIcon(cardBrand)" :alt="cardBrand" class="card-icon" />
        </div>
      </div>
      <div v-if="validationErrors.cardNumber" class="error-message">{{ validationErrors.cardNumber }}</div>
    </div>

    <div class="mt-2">
      <div class="row">
        <!-- Expiration Date -->
        <div class="form-group col-lg-3">
          <label :style="{'font-family': fontFamily, 'color': textColor}">
            {{ $t('payment_request.expiration') }}
          </label>
          <input
            ref="expiryInput"
            :style="{'color': textColor, 'border-color': getBorderColor('expiry')}"
            :class="{'error': validationErrors.expiry, 'valid': isExpiryValid}"
            class="form-control"
            placeholder="MM/YY"
            v-model="expiry"
            @input="onExpiryChange"
            @blur="validateExpiry"
            maxlength="5"
            autocomplete="cc-exp"
          />
          <div v-if="validationErrors.expiry" class="error-message">{{ validationErrors.expiry }}</div>
        </div>

        <!-- CVV -->
        <div class="form-group col-lg-3">
          <label :style="{'font-family': fontFamily, 'color': textColor}">
            {{ $t('payment_request.cvv') }}
          </label>
          <input
            ref="cvvInput"
            :style="{'color': textColor, 'border-color': getBorderColor('cvv')}"
            :class="{'error': validationErrors.cvv, 'valid': isCvvValid}"
            class="form-control"
            :placeholder="cvvPlaceholder"
            v-model="cvv"
            @input="onCvvChange"
            @blur="validateCvv"
            :maxlength="cvvMaxLength"
            type="tel"
            autocomplete="cc-csc"
          />
          <div v-if="validationErrors.cvv" class="error-message">{{ validationErrors.cvv }}</div>
        </div>

        <!-- ZIP Code -->
        <div class="form-group col-lg-4">
          <label :style="{'font-family': fontFamily, 'color': textColor}">
            {{ zipLabel }}
          </label>
          <input
            :style="{'color': textColor, 'border-color': getBorderColor('zip')}"
            :class="{'error': validationErrors.zip, 'valid': isZipValid}"
            class="form-control"
            :placeholder="zipPlaceholder"
            v-model="zip"
            @input="onZipChange"
            @blur="validateZip"
            type="text"
            autocomplete="postal-code"
          />
          <div v-if="validationErrors.zip" class="error-message">{{ validationErrors.zip }}</div>
        </div>
      </div>
    </div>

    <!-- Security Features Display -->
    <div class="security-indicators" v-if="showSecurityIndicators">
      <div class="security-item" :class="{'active': hasSecureConnection}">
        <i class="fas fa-lock"></i>
        <span>{{ $t('payment_request.secureConnection') }}</span>
      </div>
      <div class="security-item" :class="{'active': hasValidCard}">
        <i class="fas fa-credit-card"></i>
        <span>{{ $t('payment_request.validCard') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Cleave from 'cleave.js'
import valid from 'card-validator'

export default {
  name: 'EnhancedPaymentForm',
  props: {
    fontFamily: String,
    textColor: String,
    borderColor: String,
    zipLabel: {
      type: String,
      default: 'Billing postcode'
    },
    zipPlaceholder: {
      type: String,
      default: 'Enter postcode'
    },
    showSecurityIndicators: {
      type: Boolean,
      default: true
    },
    apiLoginId: String,
    publicClientKey: String,
    isLive: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      cardNumber: '',
      expiry: '',
      cvv: '',
      zip: '',
      cardBrand: null,
      cleaveCardNumber: null,
      cleaveExpiry: null,
      validationErrors: {
        cardNumber: '',
        expiry: '',
        cvv: '',
        zip: ''
      },
      isCardNumberValid: false,
      isExpiryValid: false,
      isCvvValid: false,
      isZipValid: false,
      acceptJsReady: false,
      cardTypes: {
        visa: {
          icon: '/icons/card-logos/visa.svg'
        },
        mastercard: {
          icon: '/icons/card-logos/mastercard.svg'
        },
        'american-express': {
          icon: '/icons/card-logos/amex.svg'
        },
        discover: {
          icon: '/icons/card-logos/discover.svg'
        },
        'diners-club': {
          icon: '/icons/card-logos/diners.svg'
        },
        jcb: {
          icon: '/icons/card-logos/jcb.svg'
        },
        unionpay: {
          icon: '/icons/card-logos/unionpay.svg'
        },
        maestro: {
          icon: '/icons/card-logos/maestro.svg'
        }
      }
    }
  },
  computed: {
    cvvMaxLength() {
      return this.getCardMaxCvvLength()
    },
    cvvPlaceholder() {
      const length = this.getCardMaxCvvLength()
      return length === 4 ? '1234' : '123'
    },
    hasSecureConnection() {
      return location.protocol === 'https:'
    },
    hasValidCard() {
      return this.isCardNumberValid && this.isExpiryValid && this.isCvvValid
    }
  },
  watch: {
    apiLoginId() { this.loadAcceptJs() },
    publicClientKey() { this.loadAcceptJs() }
  },
  mounted() {
    this.initializeCleave()
    this.loadAcceptJs()
  },
  beforeDestroy() {
    if (this.cleaveCardNumber) this.cleaveCardNumber.destroy()
    if (this.cleaveExpiry) this.cleaveExpiry.destroy()
    const script = document.getElementById('accept-js-script')
    if (script) script.remove()
  },
  methods: {
    initializeCleave() {
      // Initialize card number formatting
      this.cleaveCardNumber = new Cleave(this.$refs.cardNumberInput, {
        creditCard: true,
        onCreditCardTypeChanged: (type) => {
          // Cleave provides initial detection, but we'll validate with card-validator
          this.updateCardBrand()
        }
      })

      // Initialize expiry date formatting
      this.cleaveExpiry = new Cleave(this.$refs.expiryInput, {
        date: true,
        datePattern: ['m', 'y']
      })
    },

    onCardNumberChange() {
      this.validationErrors.cardNumber = ''
      this.updateCardBrand()
    },

    onExpiryChange() {
      this.validationErrors.expiry = ''
    },

    onCvvChange() {
      this.validationErrors.cvv = ''
      // Only allow numeric input
      this.cvv = this.cvv.replace(/\D/g, '')
    },

    onZipChange() {
      this.validationErrors.zip = ''
    },

    updateCardBrand() {
      const number = this.cardNumber.replace(/\s/g, '')
      if (number.length >= 4) {
        const cardValidation = valid.number(number)
        if (cardValidation.card) {
          this.cardBrand = cardValidation.card.type
        } else {
          this.cardBrand = null
        }
      } else {
        this.cardBrand = null
      }
    },

    validateCardNumber() {
      const number = this.cardNumber.replace(/\s/g, '')

      if (!number) {
        this.validationErrors.cardNumber = this.$t('payment_request.errors.required')
        this.isCardNumberValid = false
        return false
      }

      // Use card-validator for more robust validation
      const cardValidation = valid.number(number)

      if (!cardValidation.isValid) {
        this.validationErrors.cardNumber = this.$t('payment_request.errors.invalidCardNumber')
        this.isCardNumberValid = false
        return false
      }

      // Update card brand from card-validator (more accurate than Cleave)
      if (cardValidation.card) {
        this.cardBrand = cardValidation.card.type
      }

      this.isCardNumberValid = true
      return true
    },

    validateExpiry() {
      if (!this.expiry) {
        this.validationErrors.expiry = this.$t('payment_request.errors.required')
        this.isExpiryValid = false
        return false
      }

      // Use card-validator for more robust expiry validation
      const expiryValidation = valid.expirationDate(this.expiry)

      if (!expiryValidation.isValid) {
        if (expiryValidation.isPastMonth) {
          this.validationErrors.expiry = this.$t('payment_request.errors.cardExpired')
        } else {
          this.validationErrors.expiry = this.$t('payment_request.errors.invalidFormat')
        }
        this.isExpiryValid = false
        return false
      }

      this.isExpiryValid = true
      return true
    },

    validateCvv() {
      if (!this.cvv) {
        this.validationErrors.cvv = this.$t('payment_request.errors.required')
        this.isCvvValid = false
        return false
      }

      // Use card-validator for CVV validation with card type awareness
      const cvvValidation = valid.cvv(this.cvv, this.getCardMaxCvvLength())

      if (!cvvValidation.isValid) {
        const expectedLength = this.getCardMaxCvvLength()
        this.validationErrors.cvv = this.$t('payment_request.errors.invalidCvvLength', { length: expectedLength })
        this.isCvvValid = false
        return false
      }

      this.isCvvValid = true
      return true
    },

    getCardMaxCvvLength() {
      // Use card-validator to get accurate CVV length for detected card type
      const number = this.cardNumber.replace(/\s/g, '')
      if (number.length >= 4) {
        const cardValidation = valid.number(number)
        return (cardValidation.card && cardValidation.card.code && cardValidation.card.code.size) || 3
      }
      return 3
    },

    validateZip() {
      if (!this.zip) {
        this.validationErrors.zip = this.$t('payment_request.errors.required')
        this.isZipValid = false
        return false
      }

      // Use card-validator for postal code validation
      const zipValidation = valid.postalCode(this.zip)

      if (!zipValidation.isValid) {
        this.validationErrors.zip = this.$t('payment_request.errors.invalidZip')
        this.isZipValid = false
        return false
      }

      this.isZipValid = true
      return true
    },

    validateAll() {
      const isCardValid = this.validateCardNumber()
      const isExpiryValid = this.validateExpiry()
      const isCvvValid = this.validateCvv()
      const isZipValid = this.validateZip()

      return isCardValid && isExpiryValid && isCvvValid && isZipValid
    },

    getCardIcon(brand) {
      return (this.cardTypes[brand] && this.cardTypes[brand].icon) || ''
    },

    getBorderColor(field) {
      if (this.validationErrors[field]) return '#dc3545'
      if (this[`is${field.charAt(0).toUpperCase() + field.slice(1)}Valid`]) return '#28a745'
      return this.borderColor || this.textColor
    },

    getFormData() {
      if (!this.validateAll()) {
        return null
      }

      return {
        cardNumber: this.cardNumber.replace(/\s/g, ''),
        expirationDate: this.expiry,
        cardCode: this.cvv,
        zip: this.zip,
        cardBrand: this.cardBrand
      }
    },

    reset() {
      this.cardNumber = ''
      this.expiry = ''
      this.cvv = ''
      this.zip = ''
      this.cardBrand = null
      this.validationErrors = {
        cardNumber: '',
        expiry: '',
        cvv: '',
        zip: ''
      }
      this.isCardNumberValid = false
      this.isExpiryValid = false
      this.isCvvValid = false
      this.isZipValid = false
    },

    loadAcceptJs() {
      if (!this.apiLoginId) return
      if (document.getElementById('accept-js-script')) {
        if (typeof window.Accept !== 'undefined') this.acceptJsReady = true
        return
      }
      const script = document.createElement('script')
      script.id = 'accept-js-script'
      script.src = this.isLive
        ? 'https://js.authorize.net/v1/Accept.js'
        : 'https://jstest.authorize.net/v1/Accept.js'
      script.charset = 'utf-8'
      script.onload = () => { this.acceptJsReady = true }
      document.head.appendChild(script)
    },

    waitForAcceptJs(timeout = 10000) {
      return new Promise((resolve, reject) => {
        if (this.acceptJsReady && typeof window.Accept !== 'undefined') return resolve()
        let waited = 0
        const interval = 100
        const check = setInterval(() => {
          if (typeof window.Accept !== 'undefined') {
            clearInterval(check)
            this.acceptJsReady = true
            resolve()
          }
          waited += interval
          if (waited >= timeout) {
            clearInterval(check)
            reject(new Error('ACCEPT_JS_NOT_LOADED'))
          }
        }, interval)
      })
    },

    async tokenize() {
      if (!this.validateAll()) throw new Error('VALIDATION_FAILED')

      await this.waitForAcceptJs()

      // Read from DOM refs to avoid Cleave.js / v-model desync
      const cardNumber = this.$refs.cardNumberInput.value.replace(/\s/g, '')
      const expiryValue = this.$refs.expiryInput.value
      let month, year
      if (expiryValue.includes('/')) {
        month = expiryValue.split('/')[0]
        year = '20' + expiryValue.split('/')[1]
      } else {
        month = expiryValue.substring(0, 2)
        year = '20' + expiryValue.substring(2)
      }

      const secureData = {
        authData: {
          clientKey: this.publicClientKey,
          apiLoginID: this.apiLoginId
        },
        cardData: {
          cardNumber: cardNumber,
          month: month,
          year: year,
          cardCode: this.cvv
        }
      }

      return new Promise((resolve, reject) => {
        window.Accept.dispatchData(secureData, (response) => {
          if (response.messages.resultCode === 'Error') {
            reject(new Error(response.messages.message[0].text))
          } else {
            resolve({
              dataDescriptor: response.opaqueData.dataDescriptor,
              dataValue: response.opaqueData.dataValue
            })
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.enhanced-payment-form {
  /*max-width: 600px;*/
  /*margin: 0 auto;*/
}

.card-input-container {
  position: relative;
}

.card-brand-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.card-icon {
  width: 32px;
  height: 24px;
  object-fit: contain;
}

.form-control {
  border-radius: 6px;
  border: 1.5px solid;
  padding: 10px 12px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.form-control.error {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.form-control.valid {
  border-color: #28a745 !important;
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
}

.security-indicators {
  display: flex;
  gap: 20px;
  margin-top: 20px;
  font-size: 12px;
}

.security-item {
  display: flex;
  align-items: center;
  gap: 5px;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.security-item.active {
  opacity: 1;
  color: #28a745;
}

.security-item i {
  font-size: 14px;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group {
  flex: 1;
  margin-bottom: 20px;
}

.col-md-4 {
  flex: 0 0 33.333333%;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }

  .col-md-4 {
    flex: 1 1 100%;
  }
}
</style>
